import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Fade from "@mui/material/Fade";
import Slide from "@mui/material/Slide";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import InputBase from "@mui/material/InputBase";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import TeacherAvatar from "components/TeacherAvatar";
import { figmaTokens } from "assets/theme/designTokens";
import { useSoftUIController } from "context";
import { usePortalPageContext } from "context/PortalPageContext";
import { useAssistantSession } from "context/AssistantSessionContext";
import { useTranslate } from "utils/i18n";
import { getUserRoleBucket } from "assistant/userRole";
import {
  buildWelcomeMessage,
  loadProactiveInsights,
  processMessage,
  processAction,
  executeAssistantWriteCommand,
  getQuickPrompts,
} from "assistant/engine";
import { isLlmEnabled, checkLlmHealth } from "assistant/llmClient";
import { enhanceWithLlm, llmErrorMessage } from "assistant/hybridEngine";
import {
  buildConversationHistory,
  extractLastTeacherFromMessages,
} from "assistant/conversationContext";
import { AI_MODES, useAssistantAiMode } from "utils/assistantAiMode";
import AssistantAiModeDialog from "components/PortalAssistant/AssistantAiModeDialog";
import { startStreamReveal, prepareStreamMessage } from "utils/streamTextReveal";
import { isAssistantTourDone } from "assistant/onboarding";
import { MarkdownContent } from "components/PortalAssistant/markdown";
import TeacherProfileCard from "components/PortalAssistant/TeacherProfileCard";
import CommandConfirmCard from "components/PortalAssistant/CommandConfirmCard";
import AssistantWalkthrough from "components/PortalAssistant/AssistantWalkthrough";

const PANEL_WIDTH = 400;
const PANEL_HEIGHT = 580;
const ASSISTANT_ROUTE = "/assistant";

const TOUR_PROMPT_RE = /\b(quick tour|take a tour|visite guidée|visite guidee|guided tour)\b/i;

/** From full-screen assistant → target page + floating dock. From dock → optional close. */
function navigateFromAssistant(route, { isAssistantPage, navigate, setDockOpen, closeDock = false }) {
  navigate(route);
  if (isAssistantPage) {
    setDockOpen(true);
  } else if (closeDock) {
    setDockOpen(false);
  }
}

function messageHasResultBlocks(message) {
  if (!message) return false;
  return Boolean(
    message.teacherProfile ||
      message.cards?.length ||
      message.insights?.length ||
      message.capabilityCards?.length ||
      message.attendanceResults?.length ||
      message.teacherResults?.length ||
      message.storyTimeline?.length ||
      message.commandConfirm
  );
}

function GeneratingIndicator({ t, variant = "llm" }) {
  const label =
    variant === "stream"
      ? t("assistant.writing_answer", "Writing your answer…")
      : t("assistant.generating_summary", "Still generating your answer…");

  return (
    <SoftBox
      mt={1.25}
      display="inline-flex"
      alignItems="center"
      gap={1}
      px={1.25}
      py={0.65}
      borderRadius="999px"
      sx={{
        bgcolor: "rgba(102, 126, 234, 0.07)",
        border: "1px solid rgba(102, 126, 234, 0.16)",
        animation: "assistantGenPulse 2.4s ease-in-out infinite",
        "@keyframes assistantGenPulse": {
          "0%, 100%": { opacity: 1, boxShadow: "0 0 0 0 rgba(102, 126, 234, 0)" },
          "50%": { opacity: 0.92, boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.08)" },
        },
      }}
    >
      <SoftBox display="flex" alignItems="center" gap={0.45} aria-hidden>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: figmaTokens.accent,
              animation: "assistantGenDot 1.1s ease-in-out infinite",
              animationDelay: `${i * 0.16}s`,
              "@keyframes assistantGenDot": {
                "0%, 80%, 100%": { transform: "translateY(0)", opacity: 0.35 },
                "40%": { transform: "translateY(-3px)", opacity: 1 },
              },
            }}
          />
        ))}
      </SoftBox>
      <SoftTypography variant="caption" fontWeight="medium" sx={{ color: figmaTokens.textSecondary }}>
        {label}
      </SoftTypography>
    </SoftBox>
  );
}

GeneratingIndicator.propTypes = {
  t: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["llm", "stream"]),
};

function StreamingCursor() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: "2px",
        height: "0.95em",
        ml: 0.25,
        verticalAlign: "text-bottom",
        bgcolor: figmaTokens.accent,
        animation: "assistantCursor 0.9s step-end infinite",
        "@keyframes assistantCursor": {
          "0%, 100%": { opacity: 1 },
          "50%": { opacity: 0 },
        },
      }}
    />
  );
}

function TypingIndicator() {
  return (
    <SoftBox display="flex" alignItems="center" gap={0.5} py={1} px={1.5}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: figmaTokens.accent,
            opacity: 0.5,
            animation: "assistantDot 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.15}s`,
            "@keyframes assistantDot": {
              "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.35 },
              "40%": { transform: "scale(1)", opacity: 1 },
            },
          }}
        />
      ))}
    </SoftBox>
  );
}

function StatCards({ cards }) {
  if (!cards?.length) return null;
  return (
    <SoftBox display="flex" flexWrap="wrap" gap={1} mt={1.5}>
      {cards.map((card) => (
        <SoftBox
          key={card.id || card.label}
          sx={{
            minWidth: 88,
            px: 1.5,
            py: 1,
            borderRadius: "12px",
            border: `1px solid ${card.highlight ? "rgba(240, 177, 0, 0.45)" : figmaTokens.borderSubtle}`,
            bgcolor: card.highlight ? "rgba(240, 177, 0, 0.08)" : figmaTokens.pageBg,
          }}
        >
          <SoftTypography variant="caption" color="secondary" fontWeight="medium" display="block">
            {card.label}
          </SoftTypography>
          <SoftTypography variant="h6" fontWeight="bold" color="dark">
            {card.value}
          </SoftTypography>
        </SoftBox>
      ))}
    </SoftBox>
  );
}
StatCards.propTypes = { cards: PropTypes.arrayOf(PropTypes.object) };

function InsightCards({ insights, onAction }) {
  if (!insights?.length) return null;
  const severityColor = (s) =>
    s === "warning" ? figmaTokens.badgePending : s === "error" ? figmaTokens.badgeDanger : figmaTokens.accent;

  return (
    <SoftBox display="flex" flexDirection="column" gap={1} mt={1.5}>
      {insights.map((ins) => (
        <SoftBox
          key={ins.id || ins.title}
          sx={{
            p: 1.25,
            borderRadius: "12px",
            borderLeft: `3px solid ${severityColor(ins.severity)}`,
            bgcolor: figmaTokens.pageBg,
            border: `1px solid ${figmaTokens.borderSubtle}`,
            borderLeftWidth: 3,
            borderLeftColor: severityColor(ins.severity),
          }}
        >
          <SoftTypography variant="button" fontWeight="medium" color="dark" display="block">
            {ins.title}
          </SoftTypography>
          {ins.actionLabel && ins.route && (
            <Chip
              size="small"
              label={ins.actionLabel}
              onClick={() => onAction({ route: ins.route, autoNavigate: true })}
              sx={{
                mt: 0.75,
                height: 26,
                fontSize: "0.72rem",
                fontWeight: 600,
                bgcolor: "rgba(102, 126, 234, 0.12)",
                color: figmaTokens.accent,
                "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
              }}
            />
          )}
        </SoftBox>
      ))}
    </SoftBox>
  );
}
InsightCards.propTypes = {
  insights: PropTypes.arrayOf(PropTypes.object),
  onAction: PropTypes.func.isRequired,
};

function WizardBlock({ wizard }) {
  if (!wizard) return null;
  const progress = ((wizard.stepIndex + 1) / wizard.totalSteps) * 100;
  return (
    <SoftBox
      mt={1.5}
      p={1.5}
      borderRadius="14px"
      sx={{
        background: `linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.06) 100%)`,
        border: `1px solid rgba(102, 126, 234, 0.2)`,
      }}
    >
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
        <SoftTypography variant="caption" fontWeight="bold" color="info">
          {wizard.title}
        </SoftTypography>
        <SoftTypography variant="caption" color="secondary">
          {wizard.stepIndex + 1}/{wizard.totalSteps}
        </SoftTypography>
      </SoftBox>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 5,
          borderRadius: 3,
          mb: 1,
          bgcolor: "rgba(102, 126, 234, 0.15)",
          "& .MuiLinearProgress-bar": { background: figmaTokens.gradientCSS, borderRadius: 3 },
        }}
      />
      <SoftTypography variant="button" color="text" sx={{ lineHeight: 1.55 }}>
        {wizard.body}
      </SoftTypography>
    </SoftBox>
  );
}
WizardBlock.propTypes = { wizard: PropTypes.object };

function PickResults({ items, pickKind, commandDraft, onAction, label }) {
  if (!items?.length) return null;
  return (
    <SoftBox mt={1.5}>
      {label && (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium" display="block" mb={0.75}>
          {label}
        </SoftTypography>
      )}
      <SoftBox display="flex" flexDirection="column" gap={0.75}>
        {items.map((row) => (
          <SoftBox
            key={row.id || row.reg || row.name}
            role="button"
            tabIndex={0}
            onClick={() => {
              if (!row.id) return;
              if (pickKind === "establishment") {
                onAction?.({ action: `command-pick-establishment:${row.id}`, commandDraft });
              } else if (pickKind === "leave") {
                onAction?.({ action: `command-pick-leave:${row.id}`, commandDraft });
              } else if (pickKind === "leave_type") {
                onAction?.({
                  action: `command-pick-leave-type:${row.id}`,
                  commandDraft,
                  leaveTypeName: row.name,
                });
              }
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              if (!row.id) return;
              if (pickKind === "establishment") {
                onAction?.({ action: `command-pick-establishment:${row.id}`, commandDraft });
              } else if (pickKind === "leave") {
                onAction?.({ action: `command-pick-leave:${row.id}`, commandDraft });
              } else if (pickKind === "leave_type") {
                onAction?.({
                  action: `command-pick-leave-type:${row.id}`,
                  commandDraft,
                  leaveTypeName: row.name,
                });
              }
            }}
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: "12px",
              bgcolor: figmaTokens.pageBg,
              border: `1px solid ${figmaTokens.borderSubtle}`,
              cursor: "pointer",
              "&:hover": {
                borderColor: "rgba(102, 126, 234, 0.45)",
                boxShadow: "0 4px 14px rgba(102, 126, 234, 0.12)",
              },
            }}
          >
            <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <SoftBox minWidth={0}>
                <SoftTypography variant="button" fontWeight="bold" color="dark" noWrap>
                  {row.name}
                </SoftTypography>
                <SoftTypography variant="caption" color="secondary" display="block" noWrap>
                  {[row.reg, row.detail].filter(Boolean).join(" · ")}
                </SoftTypography>
              </SoftBox>
              <Icon sx={{ color: figmaTokens.accent, fontSize: "1.1rem !important" }}>chevron_right</Icon>
            </SoftBox>
          </SoftBox>
        ))}
      </SoftBox>
    </SoftBox>
  );
}
PickResults.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  pickKind: PropTypes.string,
  commandDraft: PropTypes.object,
  onAction: PropTypes.func,
  label: PropTypes.string,
};

function TeacherResults({ items, selectable = true, onAction, commandDraft }) {
  const { language } = useTranslate();
  if (!items?.length) return null;
  const canOpen = selectable !== false;
  const forCommand = Boolean(commandDraft?.command);
  return (
    <SoftBox mt={1.5}>
      {canOpen && (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium" display="block" mb={0.75}>
          {forCommand
            ? language === "en"
              ? "Click a teacher to continue the command"
              : "Cliquez sur une enseignante pour continuer la commande"
            : language === "en"
              ? "Click a teacher to open their full profile card"
              : "Cliquez sur une enseignante pour ouvrir sa fiche complète"}
        </SoftTypography>
      )}
      <SoftBox display="flex" flexDirection="column" gap={0.85}>
        {items.map((teacher) => {
          const openStory = () => {
            if (canOpen && teacher.id) {
              onAction?.({
                action: `teacher-story:${teacher.id}`,
                ...(forCommand ? { commandDraft } : null),
              });
            }
          };
          return (
            <SoftBox
              key={teacher.id || teacher.reg}
              role={canOpen && teacher.id ? "button" : undefined}
              tabIndex={canOpen && teacher.id ? 0 : undefined}
              onClick={openStory}
              onKeyDown={(e) => {
                if (!canOpen || !teacher.id) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openStory();
                }
              }}
              sx={{
                px: 1.35,
                py: 1.1,
                borderRadius: "14px",
                background: `linear-gradient(135deg, rgba(102, 126, 234, 0.06) 0%, ${figmaTokens.pageBg} 55%)`,
                border: `1px solid ${figmaTokens.borderSubtle}`,
                cursor: canOpen && teacher.id ? "pointer" : "default",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
                ...(canOpen &&
                  teacher.id && {
                    "&:hover": {
                      borderColor: "rgba(102, 126, 234, 0.45)",
                      boxShadow: "0 8px 22px rgba(102, 126, 234, 0.14)",
                      transform: "translateY(-1px)",
                    },
                    "&:focus-visible": {
                      outline: "none",
                      borderColor: figmaTokens.accent,
                      boxShadow: `0 0 0 3px ${figmaTokens.focusRing}`,
                    },
                  }),
              }}
            >
              <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <SoftBox display="flex" alignItems="center" gap={1.25} flex={1} minWidth={0}>
                  <TeacherAvatar
                    teacher={teacher}
                    profilePic={teacher.profilePic}
                    hasProfilePic={teacher.hasProfilePic}
                    size="md"
                    preview={false}
                  />
                  <SoftBox flex={1} minWidth={0}>
                    <SoftTypography variant="button" fontWeight="bold" color="dark" noWrap>
                      {teacher.name}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="secondary" display="block" noWrap>
                      {[teacher.reg, teacher.phone !== "—" ? teacher.phone : null, teacher.degree]
                        .filter(Boolean)
                        .join(" · ")}
                    </SoftTypography>
                    <SoftBox display="flex" flexWrap="wrap" gap={0.45} mt={0.65}>
                      {teacher.score != null && (
                        <Chip
                          size="small"
                          label={`★ ${teacher.score}`}
                          sx={{
                            height: 20,
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            bgcolor: "rgba(102, 126, 234, 0.12)",
                            color: figmaTokens.accent,
                          }}
                        />
                      )}
                      <Chip
                        size="small"
                        label={
                          teacher.isLicensed
                            ? language === "en"
                              ? "Licensed"
                              : "Licenciée"
                            : language === "en"
                              ? "Unlicensed"
                              : "Non licenciée"
                        }
                        sx={{
                          height: 20,
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          bgcolor: teacher.isLicensed ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.1)",
                          color: teacher.isLicensed ? "#047857" : "#b91c1c",
                        }}
                      />
                      {teacher.status ? (
                        <Chip
                          size="small"
                          label={teacher.status}
                          sx={{
                            height: 20,
                            fontSize: "0.62rem",
                            fontWeight: 600,
                            bgcolor: "rgba(15, 23, 42, 0.06)",
                            color: figmaTokens.textSecondary,
                          }}
                        />
                      ) : null}
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
                {canOpen && teacher.id && (
                  <Icon sx={{ color: figmaTokens.accent, fontSize: "1.15rem !important", flexShrink: 0 }}>
                    chevron_right
                  </Icon>
                )}
              </SoftBox>
            </SoftBox>
          );
        })}
      </SoftBox>
    </SoftBox>
  );
}
TeacherResults.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  selectable: PropTypes.bool,
  onAction: PropTypes.func,
  commandDraft: PropTypes.object,
};

function AttendanceResults({ items, label }) {
  if (!items?.length) return null;
  return (
    <SoftBox mt={1.5}>
      {label && (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium" display="block" mb={0.75}>
          {label}
        </SoftTypography>
      )}
      <SoftBox display="flex" flexDirection="column" gap={0.75}>
        {items.map((row, idx) => (
          <SoftBox
            key={row.id || row.reg || row.name || idx}
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: "10px",
              bgcolor: figmaTokens.pageBg,
              border: `1px solid ${figmaTokens.borderSubtle}`,
            }}
          >
            <SoftTypography variant="button" fontWeight="bold" color="dark">
              {row.name}
            </SoftTypography>
            <SoftTypography variant="caption" color="secondary" display="block">
              {[row.reg, row.detail].filter(Boolean).join(" · ")}
            </SoftTypography>
          </SoftBox>
        ))}
      </SoftBox>
    </SoftBox>
  );
}
AttendanceResults.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
};

function StoryTimeline({ items }) {
  if (!items?.length) return null;
  return (
    <SoftBox mt={1.5} display="flex" flexDirection="column" gap={0.75}>
      {items.map((step) => (
        <SoftBox
          key={step.id}
          sx={{
            px: 1.25,
            py: 1,
            borderRadius: "10px",
            bgcolor: figmaTokens.pageBg,
            border: `1px solid ${figmaTokens.borderSubtle}`,
            borderLeft: `3px solid ${figmaTokens.accent}`,
          }}
        >
          <SoftTypography variant="caption" color="secondary" fontWeight="medium" display="block">
            {step.title}
          </SoftTypography>
          <SoftTypography variant="button" fontWeight="regular" color="dark" sx={{ fontSize: "0.78rem", lineHeight: 1.5 }}>
            {step.detail}
          </SoftTypography>
        </SoftBox>
      ))}
    </SoftBox>
  );
}
StoryTimeline.propTypes = { items: PropTypes.arrayOf(PropTypes.object) };

function CapabilityTiles({ items, onAction }) {
  if (!items?.length) return null;
  return (
    <SoftBox
      mt={1.5}
      display="flex"
      gap={0.75}
      sx={{ overflowX: "auto", pb: 0.5, mx: -0.5, px: 0.5 }}
    >
      {items.map((item) => (
        <Tooltip key={item.id} title={item.description || item.title} placement="top">
          <Chip
            icon={<Icon sx={{ fontSize: "0.95rem !important" }}>{item.icon || "touch_app"}</Icon>}
            label={item.title}
            size="small"
            onClick={() => onAction({ action: `suggest:${item.message}` })}
            sx={{
              height: 32,
              flexShrink: 0,
              fontWeight: 600,
              fontSize: "0.7rem",
              bgcolor: figmaTokens.pageBg,
              border: `1px solid ${figmaTokens.borderSubtle}`,
              "& .MuiChip-icon": { color: figmaTokens.accent },
              "&:hover": { bgcolor: "rgba(102, 126, 234, 0.1)", borderColor: "rgba(102, 126, 234, 0.35)" },
            }}
          />
        </Tooltip>
      ))}
    </SoftBox>
  );
}
CapabilityTiles.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onAction: PropTypes.func.isRequired,
};

function MessageBubble({ message, onAction }) {
  const isUser = message.role === "user";
  const { t } = useTranslate();

  return (
    <SoftBox
      display="flex"
      justifyContent={isUser ? "flex-end" : "flex-start"}
      mb={1.5}
      px={0.5}
    >
      {!isUser && (
        <SoftBox
          mr={1}
          mt={0.25}
          width={28}
          height={28}
          borderRadius="10px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ background: figmaTokens.gradientDiagonalCSS, flexShrink: 0 }}
        >
          <Icon sx={{ color: "#fff", fontSize: "1rem !important" }}>smart_toy</Icon>
        </SoftBox>
      )}
      <SoftBox
        maxWidth={
          message.teacherProfile ||
          message.commandConfirm ||
          message.type === "story" ||
          message.teacherResults ||
          message.pickResults
            ? "100%"
            : "85%"
        }
        width={message.teacherProfile || message.commandConfirm ? "100%" : undefined}
      >
        <SoftBox
          px={1.75}
          py={1.25}
          borderRadius={isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px"}
          sx={{
            bgcolor: isUser ? figmaTokens.sidebarBg : figmaTokens.surface,
            color: isUser ? "#fff" : figmaTokens.textBody,
            border: isUser ? "none" : `1px solid ${figmaTokens.borderSubtle}`,
            boxShadow: isUser ? "0 4px 14px rgba(30, 41, 59, 0.2)" : "0 2px 8px rgba(15, 23, 42, 0.04)",
          }}
        >
          {message.content && !message.teacherProfile && (
            <SoftBox component="span" display="inline">
              <MarkdownContent text={message.content} isUser={isUser} />
              {message.isStreaming && <StreamingCursor />}
            </SoftBox>
          )}
          {message.content && message.teacherProfile && (
            <SoftTypography
              variant="button"
              fontWeight="medium"
              display="block"
              mb={1}
              sx={{ color: figmaTokens.textSecondary, fontSize: "0.8rem", lineHeight: 1.45 }}
            >
              {String(message.content).replace(/\*\*/g, "")}
              {message.isStreaming && <StreamingCursor />}
            </SoftTypography>
          )}
          {!isUser && message.isStreaming && !message.content && (
            <SoftBox display="flex" alignItems="center" gap={1} py={0.25}>
              <CircularProgress size={14} thickness={5} sx={{ color: figmaTokens.accent }} />
              <SoftTypography variant="caption" fontWeight="medium" sx={{ color: figmaTokens.textSecondary }}>
                {t("assistant.llm_streaming", "One moment…")}
              </SoftTypography>
            </SoftBox>
          )}
          {!isUser && message.llmPending && !message.content && !message.isStreaming && (
            <SoftBox
              mt={message.content || message.teacherProfile ? 1 : 0}
              mb={0.5}
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                px: 1,
                py: 0.65,
                borderRadius: "10px",
                bgcolor: "rgba(102, 126, 234, 0.08)",
                border: "1px solid rgba(102, 126, 234, 0.2)",
              }}
            >
              <CircularProgress size={14} thickness={5} sx={{ color: figmaTokens.accent }} />
              <SoftTypography variant="caption" fontWeight="medium" sx={{ color: figmaTokens.accent }}>
                {message.content
                  ? t("assistant.llm_writing", "One moment…")
                  : t("assistant.llm_streaming", "One moment…")}
              </SoftTypography>
            </SoftBox>
          )}
          {!isUser && message.teacherProfile && (
            <TeacherProfileCard profile={message.teacherProfile} onAction={onAction} />
          )}
          {!isUser && message.commandConfirm && (
            <CommandConfirmCard
              confirm={message.commandConfirm}
              disabled={Boolean(message.commandConfirm.consumed)}
              onAction={onAction}
            />
          )}
          {!isUser && !message.teacherProfile && <StatCards cards={message.cards} />}
          {!isUser && <InsightCards insights={message.insights} onAction={onAction} />}
          {!isUser && <WizardBlock wizard={message.wizard} />}
          {!isUser && (
            <TeacherResults
              items={message.teacherResults}
              selectable={message.teacherResultsSelectable !== false}
              onAction={onAction}
              commandDraft={message.commandDraft}
            />
          )}
          {!isUser && (
            <PickResults
              items={message.pickResults}
              pickKind={message.pickKind}
              commandDraft={message.commandDraft}
              onAction={onAction}
            />
          )}
          {!isUser && <AttendanceResults items={message.attendanceResults} label={message.attendanceLabel} />}
          {!isUser && !message.teacherProfile && <StoryTimeline items={message.storyTimeline} />}
          {!isUser && <CapabilityTiles items={message.capabilityCards} onAction={onAction} />}
          {!isUser && message.llmPending && messageHasResultBlocks(message) && (
            <GeneratingIndicator t={t} variant="llm" />
          )}
          {!isUser && message.isStreaming && messageHasResultBlocks(message) && !message.llmPending && (
            <GeneratingIndicator t={t} variant="stream" />
          )}
          {!isUser && !message.teacherProfile && message.actions?.length > 0 && (
            <SoftBox display="flex" flexWrap="wrap" gap={0.75} mt={1.5}>
              {message.actions.map((act) => (
                <Chip
                  key={act.id}
                  label={act.label}
                  size="small"
                  onClick={() => onAction(act)}
                  sx={{
                    height: 28,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    ...(act.variant === "primary"
                      ? {
                          background: figmaTokens.gradientCSS,
                          color: "#fff",
                          border: "none",
                          "&:hover": { opacity: 0.92 },
                        }
                      : {
                          bgcolor: figmaTokens.pageBg,
                          border: `1px solid ${figmaTokens.borderSubtle}`,
                        }),
                  }}
                />
              ))}
            </SoftBox>
          )}
          {!isUser && message.suggestions?.length > 0 && (
            <SoftBox display="flex" flexWrap="wrap" gap={0.5} mt={1}>
              {message.suggestions.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  variant="outlined"
                  onClick={() => onAction({ action: `suggest:${s}` })}
                  sx={{ height: 24, fontSize: "0.68rem", borderColor: figmaTokens.borderSubtle }}
                />
              ))}
            </SoftBox>
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

MessageBubble.propTypes = {
  message: PropTypes.object.isRequired,
  onAction: PropTypes.func.isRequired,
};

function useAssistantController({ active }) {
  const [controller] = useSoftUIController();
  const { pageContext } = usePortalPageContext();
  const { t, language } = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useAssistantSession();
  const {
    dockOpen,
    setDockOpen,
    input,
    setInput,
    messages,
    setMessages,
    loading,
    setLoading,
    proactiveInsights,
    setProactiveInsights,
    wizardState,
    setWizardState,
    insightsLoading,
    setInsightsLoading,
    llmOnline,
    setLlmOnline,
    lastWorkPath,
    setLastWorkPath,
  } = session;

  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);
  const streamCleanupRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastMessageCountRef = useRef(0);
  const [tourOpen, setTourOpen] = useState(false);
  const tourPromptedRef = useRef(false);

  const isAssistantPage = location.pathname === ASSISTANT_ROUTE || location.pathname.startsWith(`${ASSISTANT_ROUTE}/`);
  const token = localStorage.getItem("tk446655440000");
  const isAuthPage = ["/sign-in", "/sign-up", "/login"].some((p) => location.pathname.startsWith(p));
  const visible = Boolean(token) && !isAuthPage;

  useEffect(() => {
    if (!isAssistantPage && location.pathname) {
      setLastWorkPath(location.pathname);
    }
  }, [isAssistantPage, location.pathname, setLastWorkPath]);

  const roleBucket = useMemo(() => getUserRoleBucket(controller.user), [controller.user]);
  const llmBuildEnabled = isLlmEnabled();
  const { aiMode, setAiMode, aiRoutingEnabled, useGemini } = useAssistantAiMode({ llmBuildEnabled });
  const [geminiConfigured, setGeminiConfigured] = useState(null);
  const [ollamaOnline, setOllamaOnline] = useState(null);
  const [geminiOnline, setGeminiOnline] = useState(null);
  const [modeDialogOpen, setModeDialogOpen] = useState(false);

  const engineContext = useMemo(
    () => ({
      language,
      user: controller.user,
      pathname: isAssistantPage ? lastWorkPath : location.pathname,
      pageContext,
      roleBucket,
      wizardState,
      t,
      conversationHistory: buildConversationHistory(messages),
      lastTeacher: extractLastTeacherFromMessages(messages),
      messages,
      aiRoutingEnabled: aiRoutingEnabled && llmBuildEnabled,
      useGemini,
    }),
    [language, controller.user, isAssistantPage, lastWorkPath, location.pathname, pageContext, roleBucket, wizardState, t, messages, aiRoutingEnabled, useGemini]
  );

  const scrollToBottom = useCallback((behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const handleChatScroll = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
  }, []);

  useEffect(() => {
    if (!active) return;

    const count = messages.length;
    const addedMessage = count > lastMessageCountRef.current;
    lastMessageCountRef.current = count;

    if (addedMessage || loading) {
      stickToBottomRef.current = true;
      scrollToBottom(addedMessage ? "smooth" : "auto");
    }
  }, [active, messages.length, loading, scrollToBottom]);

  const refreshInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const insights = await loadProactiveInsights(engineContext);
      setProactiveInsights(insights);
    } catch {
      setProactiveInsights([]);
    } finally {
      setInsightsLoading(false);
    }
  }, [engineContext, setInsightsLoading, setProactiveInsights]);

  useEffect(() => {
    if (active && llmBuildEnabled) {
      checkLlmHealth({ useGemini }).then((h) => {
        setLlmOnline(Boolean(h.available));
        setGeminiConfigured(h.geminiConfigured ?? h.gemini !== "missing_key");
        setGeminiOnline(h.gemini === "ok");
        setOllamaOnline(h.ollama === "ok");
      });
    } else if (!llmBuildEnabled) {
      setLlmOnline(null);
      setGeminiConfigured(null);
      setGeminiOnline(null);
      setOllamaOnline(null);
    }
  }, [active, useGemini, llmBuildEnabled]);

  useEffect(() => {
    if (visible && active && proactiveInsights.length === 0 && !insightsLoading) {
      refreshInsights();
    }
  }, [visible, active, proactiveInsights.length, insightsLoading, refreshInsights]);

  useEffect(() => {
    if (active && messages.length === 0) {
      const welcome = buildWelcomeMessage(engineContext);
      const prepared = prepareStreamMessage(welcome);
      setMessages([prepared]);
      streamCleanupRef.current?.();
      streamCleanupRef.current = startStreamReveal(prepared.id, welcome.content, setMessages);
    }
  }, [active, messages.length, engineContext, setMessages]);

  useEffect(() => {
    if (active && visible && !isAssistantTourDone() && !tourPromptedRef.current) {
      tourPromptedRef.current = true;
      const timer = setTimeout(() => setTourOpen(true), 700);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [active, visible]);

  useEffect(() => () => streamCleanupRef.current?.(), []);

  useEffect(() => {
    if (active) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [active]);

  const applyLlmEnhance = useCallback(
    async (message) => {
      if (!message?.llmPending || !message?.enhanceRequest || !isLlmEnabled() || !aiRoutingEnabled) return;
      const msgId = message.id;
      const { userMessage, ruleFacts, mode = "enhance" } = message.enhanceRequest;
      const fallbackContent = message.content;

      try {
        const narrative = await enhanceWithLlm(
          userMessage,
          engineContext,
          ruleFacts,
          mode,
          (partial) => {
            if (!partial) return;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== msgId) return m;
                return {
                  ...m,
                  content: partial,
                  type: "text",
                  isStreaming: true,
                };
              })
            );
          }
        );

        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== msgId) return m;
            if (!narrative) {
              if (message.llmStreaming && !fallbackContent) {
                const hint = llmErrorMessage({ error: "network" }, engineContext.language);
                return {
                  ...m,
                  type: "error",
                  content:
                    (engineContext.language === "en"
                      ? "I couldn't finish that response. Please try again.\n\n"
                      : "Je n'ai pas pu terminer la réponse. Veuillez réessayer.\n\n") + hint,
                  llmPending: false,
                  enhanceRequest: undefined,
                };
              }
              return { ...m, llmPending: false, enhanceRequest: undefined };
            }
            return {
              ...m,
              content: narrative,
              type: "text",
              isStreaming: false,
              llmPending: false,
              enhanceRequest: undefined,
            };
          })
        );
      } catch (err) {
        console.warn("[Assistant] background LLM enhance failed", err?.message || err);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId ? { ...m, llmPending: false, enhanceRequest: undefined } : m
          )
        );
      }
    },
    [engineContext, setMessages, aiRoutingEnabled]
  );

  const pushAssistantResult = useCallback(
    (result) => {
      if (!result) return;

      if (result.llmPending) {
        setMessages((prev) => [...prev, result]);
        void applyLlmEnhance(result);
        return;
      }

      const prepared = prepareStreamMessage(result);
      const needsStream = prepared.isStreaming && prepared.streamFull;
      setMessages((prev) => [...prev, prepared]);

      if (needsStream) {
        streamCleanupRef.current?.();
        streamCleanupRef.current = startStreamReveal(prepared.id, prepared.streamFull, setMessages);
      }
    },
    [applyLlmEnhance, setMessages]
  );

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = String(text ?? "").trim();
      if (!trimmed || loading) return;

      if (TOUR_PROMPT_RE.test(trimmed)) {
        setTourOpen(true);
        return;
      }

      stickToBottomRef.current = true;

      const userMsg = {
        id: `user-${Date.now()}`,
        role: "user",
        type: "text",
        content: trimmed,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setLoading(true);

      try {
        const result = await processMessage(trimmed, engineContext);
        if (result?.clear) {
          const welcome = result.message || buildWelcomeMessage(engineContext);
          const prepared = prepareStreamMessage(welcome);
          setMessages([prepared]);
          streamCleanupRef.current?.();
          streamCleanupRef.current = startStreamReveal(prepared.id, welcome.content, setMessages);
          setWizardState(null);
          return;
        }
        if (result) {
          pushAssistantResult(result);
          if (result.wizardState !== undefined) setWizardState(result.wizardState);
          const autoNav = result.actions?.find((a) => a.autoNavigate && a.route);
          if (autoNav) {
            setTimeout(() => {
              navigateFromAssistant(autoNav.route, {
                isAssistantPage,
                navigate,
                setDockOpen,
                closeDock: true,
              });
            }, 400);
          }
        }
      } catch (err) {
        console.error("[Assistant] sendMessage failed", err);
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: "assistant",
            type: "error",
            content:
              language === "en"
                ? "Something went wrong processing your request. Please try again."
                : "Une erreur s'est produite. Veuillez réessayer.",
            timestamp: Date.now(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [
      loading,
      engineContext,
      navigate,
      language,
      isAssistantPage,
      setMessages,
      setInput,
      setLoading,
      setWizardState,
      setDockOpen,
      pushAssistantResult,
    ]
  );

  const handleAction = useCallback(
    async (act) => {
      if (act.action?.startsWith("suggest:")) {
        sendMessage(act.action.replace("suggest:", ""));
        return;
      }
      if (act.route) {
        navigateFromAssistant(act.route, {
          isAssistantPage,
          navigate,
          setDockOpen,
          closeDock: Boolean(act.autoNavigate),
        });
        return;
      }

      // Feature 2 — execute confirmed command
      if (act.action === "command-execute") {
        setLoading(true);
        try {
          // Mark confirm card as consumed so Confirm cannot be double-clicked
          setMessages((prev) =>
            prev.map((m) =>
              m.commandConfirm?.confirmToken === act.commandConfirm?.confirmToken
                ? { ...m, commandConfirm: { ...m.commandConfirm, consumed: true } }
                : m
            )
          );
          const result = await executeAssistantWriteCommand(act.commandConfirm, engineContext);
          if (result?.message) pushAssistantResult(result.message);
        } finally {
          setLoading(false);
        }
        return;
      }

      if (act.action) {
        setLoading(true);
        try {
          const ctx = {
            ...engineContext,
            ...(act.commandDraft ? { commandDraft: act.commandDraft } : null),
            ...(act.leaveTypeName ? { leaveTypeName: act.leaveTypeName } : null),
          };
          const result = await processAction(act.action, ctx);
          if (result?.message) {
            pushAssistantResult(result.message);
            if (result.wizardState !== undefined) setWizardState(result.wizardState);
          }
        } finally {
          setLoading(false);
        }
      }
    },
    [navigate, engineContext, sendMessage, isAssistantPage, setDockOpen, setLoading, setMessages, setWizardState, pushAssistantResult]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearChat = () => {
    const welcome = buildWelcomeMessage(engineContext);
    const prepared = prepareStreamMessage(welcome);
    setMessages([prepared]);
    streamCleanupRef.current?.();
    streamCleanupRef.current = startStreamReveal(prepared.id, welcome.content, setMessages);
    setWizardState(null);
    refreshInsights();
  };

  const expandToPage = useCallback(() => {
    setDockOpen(false);
    navigate(ASSISTANT_ROUTE);
  }, [navigate, setDockOpen]);

  const collapseFromPage = useCallback(() => {
    navigate(lastWorkPath || "/dashboard");
    setDockOpen(true);
  }, [navigate, lastWorkPath, setDockOpen]);

  return {
    t,
    language,
    navigate,
    controller,
    dockOpen,
    setDockOpen,
    input,
    setInput,
    messages,
    loading,
    proactiveInsights,
    insightsLoading,
    llmOnline,
    messagesEndRef,
    inputRef,
    isAssistantPage,
    visible,
    roleBucket,
    engineContext,
    refreshInsights,
    sendMessage,
    handleAction,
    handleSubmit,
    clearChat,
    expandToPage,
    collapseFromPage,
    alertCount: proactiveInsights.filter((i) => i.severity === "warning").length,
    ...getQuickPrompts(language, roleBucket),
    aiMode,
    setAiMode,
    aiRoutingEnabled,
    useGemini,
    modeDialogOpen,
    setModeDialogOpen,
    geminiConfigured,
    geminiOnline,
    ollamaOnline,
    tourOpen,
    setTourOpen,
    chatScrollRef,
    handleChatScroll,
  };
}

function PromptChip({ tile, loading, onSend }) {
  const isCommand = tile.variant === "command";
  return (
    <Tooltip title={tile.description || tile.label} placement="top">
      <Chip
        icon={
          tile.icon ? (
            <Icon sx={{ fontSize: "0.9rem !important", color: isCommand ? "#fff !important" : undefined }}>
              {isCommand ? "bolt" : tile.icon}
            </Icon>
          ) : undefined
        }
        label={tile.label}
        size="small"
        onClick={() => onSend(tile.message)}
        disabled={loading}
        sx={{
          height: isCommand ? 34 : 32,
          fontSize: "0.7rem",
          fontWeight: isCommand ? 700 : 600,
          flexShrink: 0,
          ...(isCommand
            ? {
                background: figmaTokens.gradientCSS,
                color: "#fff",
                border: "none",
                boxShadow: "0 4px 14px rgba(102, 126, 234, 0.35)",
                "& .MuiChip-icon": { color: "#fff !important" },
                "&:hover": { opacity: 0.92, boxShadow: "0 6px 18px rgba(102, 126, 234, 0.45)" },
              }
            : {
                bgcolor: figmaTokens.pageBg,
                border: `1px solid ${figmaTokens.borderSubtle}`,
                "& .MuiChip-icon": { color: figmaTokens.accent },
                "&:hover": { bgcolor: "rgba(102, 126, 234, 0.1)", borderColor: "rgba(102, 126, 234, 0.35)" },
              }),
        }}
      />
    </Tooltip>
  );
}

PromptChip.propTypes = {
  tile: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  onSend: PropTypes.func.isRequired,
};

function getAiModeMeta(mode, t) {
  if (mode === AI_MODES.GEMINI) {
    return { icon: "cloud", label: t("assistant.mode_gemini_title", "Cloud AI (Gemini)") };
  }
  if (mode === AI_MODES.LOCAL) {
    return { icon: "memory", label: t("assistant.mode_local_title", "Local AI") };
  }
  return { icon: "bolt", label: t("assistant.mode_rules_title", "Standard") };
}

function getAiModeSubtitle(mode, { llmOnline, t }) {
  if (mode === AI_MODES.RULES) {
    return t("assistant.mode_rules_subtitle", "Rules & live data · instant");
  }
  if (llmOnline === false) {
    return t("assistant.llm_offline", "AI offline");
  }
  if (llmOnline !== true) {
    return t("assistant.llm_checking", "Connecting…");
  }
  if (mode === AI_MODES.GEMINI) {
    return t("assistant.gemini_online", "Gemini cloud · ready");
  }
  return t("assistant.mode_local_subtitle", "Local AI · on-premise");
}

function AssistantChatBody({ variant = "dock" }) {
  const isPage = variant === "page";
  // Only mounted when visible (full page, or dock Slide with mountOnEnter),
  // so always treat as active — never read dockOpen before the hook returns.
  const {
    t,
    navigate,
    setDockOpen,
    input,
    setInput,
    messages,
    loading,
    proactiveInsights,
    insightsLoading,
    llmOnline,
    messagesEndRef,
    inputRef,
    refreshInsights,
    sendMessage,
    handleAction,
    handleSubmit,
    clearChat,
    expandToPage,
    collapseFromPage,
    promptTiles,
    aiMode,
    setAiMode,
    aiRoutingEnabled,
    useGemini,
    modeDialogOpen,
    setModeDialogOpen,
    geminiConfigured,
    geminiOnline,
    ollamaOnline,
    tourOpen,
    setTourOpen,
    language,
    chatScrollRef,
    handleChatScroll,
    isAssistantPage,
  } = useAssistantController({ active: true });

  const modeMeta = getAiModeMeta(aiMode, t);

  return (
    <>
    <SoftBox
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        bgcolor: figmaTokens.surface,
        overflow: "hidden",
        ...(isPage
          ? {
              borderRadius: "18px",
              border: `1px solid ${figmaTokens.borderSubtle}`,
              boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
            }
          : null),
      }}
    >
      <SoftBox
        px={2}
        py={1.5}
        sx={{
          background: figmaTokens.gradientDiagonalCSS,
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <SoftBox display="flex" alignItems="center" justifyContent="space-between">
          <SoftBox display="flex" alignItems="center" gap={1.25}>
            <SoftBox
              width={36}
              height={36}
              borderRadius="12px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
            >
              <Icon sx={{ color: "#fff" }}>auto_awesome</Icon>
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="button" fontWeight="bold" color="white">
                {t("assistant.title", "Assistant eVerifPro")}
              </SoftTypography>
              <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", display: "block" }}>
                {isLlmEnabled()
                  ? getAiModeSubtitle(aiMode, { llmOnline, t })
                  : t("assistant.subtitle", "Secure · on-premise")}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" alignItems="center" gap={0.25}>
            <Tooltip title={t("assistant.tour_open", "Quick tour")}>
              <IconButton size="small" onClick={() => setTourOpen(true)} sx={{ color: "#fff" }}>
                <Icon fontSize="small">school</Icon>
              </IconButton>
            </Tooltip>
            {isLlmEnabled() && (
              <Tooltip title={t("assistant.mode_dialog_open", "Change response mode")}>
                <Chip
                  size="small"
                  icon={
                    <Icon sx={{ fontSize: "0.95rem !important", color: "#fff !important" }}>
                      {modeMeta.icon}
                    </Icon>
                  }
                  label={modeMeta.label}
                  onClick={() => setModeDialogOpen(true)}
                  variant="outlined"
                  sx={{
                    height: 28,
                    mr: 0.5,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.45)",
                    bgcolor: "rgba(255,255,255,0.15)",
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    "& .MuiChip-icon": { color: "#fff !important" },
                    "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
                  }}
                />
              </Tooltip>
            )}
            <Tooltip title={t("assistant.refresh", "Actualiser alertes")}>
              <IconButton size="small" onClick={refreshInsights} sx={{ color: "#fff" }}>
                {insightsLoading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <Icon fontSize="small">refresh</Icon>
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={t("assistant.clear", "Effacer")}>
              <IconButton size="small" onClick={clearChat} sx={{ color: "#fff" }}>
                <Icon fontSize="small">delete_sweep</Icon>
              </IconButton>
            </Tooltip>
            {isPage ? (
              <Tooltip title={t("assistant.collapse", "Réduire")}>
                <IconButton size="small" onClick={collapseFromPage} sx={{ color: "#fff" }}>
                  <Icon fontSize="small">close_fullscreen</Icon>
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title={t("assistant.expand", "Plein écran")}>
                  <IconButton size="small" onClick={expandToPage} sx={{ color: "#fff" }}>
                    <Icon fontSize="small">open_in_full</Icon>
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setDockOpen(false)} sx={{ color: "#fff" }}>
                  <Icon fontSize="small">close</Icon>
                </IconButton>
              </>
            )}
          </SoftBox>
        </SoftBox>
      </SoftBox>

      {proactiveInsights.length > 0 && (
        <SoftBox
          px={1.5}
          py={1}
          sx={{
            flexShrink: 0,
            bgcolor: figmaTokens.pageBg,
            borderBottom: `1px solid ${figmaTokens.borderSubtle}`,
            overflowX: "auto",
          }}
        >
          <SoftBox display="flex" gap={0.75} sx={{ minWidth: "min-content" }}>
            {proactiveInsights.slice(0, isPage ? 8 : 4).map((ins) => (
              <Chip
                key={ins.id}
                size="small"
                icon={
                  <Icon sx={{ fontSize: "0.9rem !important" }}>
                    {ins.severity === "warning" ? "warning" : "info"}
                  </Icon>
                }
                label={ins.title}
                onClick={() =>
                  ins.route &&
                  navigateFromAssistant(ins.route, { isAssistantPage, navigate, setDockOpen, closeDock: false })
                }
                sx={{
                  height: 30,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  flexShrink: 0,
                  bgcolor: ins.severity === "warning" ? "rgba(240, 177, 0, 0.12)" : "rgba(102, 126, 234, 0.1)",
                  border: `1px solid ${ins.severity === "warning" ? "rgba(240, 177, 0, 0.35)" : "rgba(102, 126, 234, 0.25)"}`,
                }}
              />
            ))}
          </SoftBox>
        </SoftBox>
      )}

      <SoftBox
        flex={1}
        ref={chatScrollRef}
        onScroll={handleChatScroll}
        sx={{
          overflowY: "auto",
          px: isPage ? { xs: 1.5, md: 3 } : 1.5,
          py: 2,
          bgcolor: figmaTokens.pageBg,
        }}
      >
        <SoftBox
          mx="auto"
          sx={{
            width: "100%",
            maxWidth: isPage ? 920 : "100%",
          }}
        >
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} onAction={handleAction} />
          ))}
          {loading && (
            <SoftBox display="flex" alignItems="center" mb={1}>
              <SoftBox
                width={28}
                height={28}
                borderRadius="10px"
                mr={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ background: figmaTokens.gradientDiagonalCSS }}
              >
                <Icon sx={{ color: "#fff", fontSize: "1rem !important" }}>smart_toy</Icon>
              </SoftBox>
              <SoftBox
                borderRadius="14px"
                bgcolor={figmaTokens.surface}
                border={`1px solid ${figmaTokens.borderSubtle}`}
              >
                <TypingIndicator />
              </SoftBox>
            </SoftBox>
          )}
          <div ref={messagesEndRef} />
        </SoftBox>
      </SoftBox>

      <SoftBox
        px={isPage ? { xs: 1.5, md: 3 } : 1.5}
        py={1}
        sx={{ flexShrink: 0, borderTop: `1px solid ${figmaTokens.borderSubtle}`, bgcolor: figmaTokens.surface }}
      >
        <SoftBox mx="auto" sx={{ maxWidth: isPage ? 920 : "100%" }}>
          <SoftTypography variant="caption" color="secondary" fontWeight="medium" display="block" mb={0.75} px={0.25}>
            {t("assistant.prompts_label", "Je peux vous aider avec")}
          </SoftTypography>
          <SoftBox display="flex" gap={0.75} sx={{ overflowX: "auto", pb: 0.5 }}>
            {(promptTiles || []).map((p) => (
              <PromptChip key={p.id || p.message} tile={p} loading={loading} onSend={sendMessage} />
            ))}
          </SoftBox>
        </SoftBox>
      </SoftBox>

      <SoftBox
        component="form"
        onSubmit={handleSubmit}
        px={isPage ? { xs: 1.5, md: 3 } : 1.5}
        py={1.25}
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${figmaTokens.borderSubtle}`,
          bgcolor: figmaTokens.surface,
        }}
      >
        <SoftBox mx="auto" sx={{ maxWidth: isPage ? 920 : "100%" }}>
          <SoftBox
            display="flex"
            alignItems="center"
            gap={1}
            px={1.5}
            py={0.75}
            borderRadius="14px"
            sx={{
              border: `1px solid ${figmaTokens.borderSubtle}`,
              bgcolor: figmaTokens.pageBg,
              "&:focus-within": {
                borderColor: figmaTokens.accent,
                boxShadow: `0 0 0 3px ${figmaTokens.focusRing}`,
              },
            }}
          >
            <InputBase
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={isPage ? 6 : 3}
              placeholder={t("assistant.placeholder", "Posez une question…")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              disabled={loading}
              sx={{ fontSize: "0.875rem", color: figmaTokens.textBody }}
            />
            <IconButton
              type="submit"
              disabled={!input.trim() || loading}
              sx={{
                width: 36,
                height: 36,
                background: input.trim() ? figmaTokens.gradientCSS : figmaTokens.borderSubtle,
                color: "#fff",
                "&:hover": { background: figmaTokens.gradientCSS, opacity: 0.9 },
                "&.Mui-disabled": { color: figmaTokens.textMuted },
              }}
            >
              <Icon fontSize="small">send</Icon>
            </IconButton>
          </SoftBox>
          <SoftTypography variant="caption" color="secondary" display="block" textAlign="center" mt={0.75}>
            {isPage
              ? t("assistant.hint_page", "Entrée envoyer · Maj+Entrée nouvelle ligne · Réduire pour le panneau flottant")
              : t("assistant.hint", "Entrée envoyer · Maj+Entrée nouvelle ligne · ⌘K basculer")}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </SoftBox>
    <AssistantAiModeDialog
      open={modeDialogOpen}
      onClose={() => setModeDialogOpen(false)}
      value={aiMode}
      onChange={setAiMode}
      t={t}
      geminiConfigured={geminiConfigured}
      geminiOnline={geminiOnline}
      ollamaOnline={ollamaOnline}
    />
    <AssistantWalkthrough
      open={tourOpen}
      language={language}
      onClose={() => setTourOpen(false)}
      onFinish={() => setTourOpen(false)}
    />
    </>
  );
}

AssistantChatBody.propTypes = {
  variant: PropTypes.oneOf(["dock", "page"]),
};

/** Full-page workspace used by /assistant layout. */
export function AssistantWorkspace() {
  return <AssistantChatBody variant="page" />;
}

function PortalAssistant() {
  const {
    t,
    dockOpen,
    setDockOpen,
    isAssistantPage,
    visible,
    alertCount,
  } = useAssistantController({ active: false });

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isAssistantPage) return;
        setDockOpen((v) => !v);
      }
      if (e.key === "Escape" && dockOpen && !isAssistantPage) setDockOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dockOpen, isAssistantPage, setDockOpen]);

  useEffect(() => {
    if (isAssistantPage) setDockOpen(false);
  }, [isAssistantPage, setDockOpen]);

  if (!visible || isAssistantPage) return null;

  return (
    <>
      <Fade in={!dockOpen}>
        <Tooltip title={t("assistant.open_tooltip", "Assistant eVerifPro (⌘K)")} placement="left">
          <SoftBox
            role="button"
            aria-label={t("assistant.open", "Ouvrir l'assistant")}
            onClick={() => setDockOpen(true)}
            sx={{
              position: "fixed",
              right: { xs: "1rem", sm: "1.5rem" },
              bottom: { xs: "1rem", sm: "1.5rem" },
              width: 58,
              height: 58,
              borderRadius: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 1300,
              background: figmaTokens.gradientDiagonalCSS,
              boxShadow: "0 12px 32px rgba(102, 126, 234, 0.45)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                transform: "scale(1.06)",
                boxShadow: "0 16px 40px rgba(102, 126, 234, 0.55)",
              },
            }}
          >
            <Icon sx={{ color: "#fff", fontSize: "1.65rem !important" }}>forum</Icon>
            {alertCount > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  minWidth: 20,
                  height: 20,
                  px: 0.5,
                  borderRadius: "10px",
                  bgcolor: figmaTokens.badgeDanger,
                  color: "#fff",
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid #fff",
                }}
              >
                {alertCount}
              </Box>
            )}
          </SoftBox>
        </Tooltip>
      </Fade>

      <Slide direction="up" in={dockOpen} mountOnEnter unmountOnExit>
        <SoftBox
          sx={{
            position: "fixed",
            right: { xs: 0, sm: "1.5rem" },
            bottom: { xs: 0, sm: "1.5rem" },
            width: { xs: "100%", sm: PANEL_WIDTH },
            height: { xs: "100%", sm: PANEL_HEIGHT },
            maxHeight: { xs: "100dvh", sm: PANEL_HEIGHT },
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",
            borderRadius: { xs: 0, sm: "20px" },
            overflow: "hidden",
            bgcolor: figmaTokens.surface,
            boxShadow: "0 28px 80px rgba(15, 23, 42, 0.22)",
            border: { xs: "none", sm: `1px solid ${figmaTokens.borderSubtle}` },
          }}
        >
          <AssistantChatBody variant="dock" />
        </SoftBox>
      </Slide>
    </>
  );
}

export default PortalAssistant;
